// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import {
  Buildings,
  CheckCircle,
  Circle,
  CircleNotch,
  FileText,
  Scroll,
  Sparkle,
  SquaresFour,
} from "@phosphor-icons/react";
import { Link as RouterLink } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2RailSection } from "./AskV2RailSection";
import { AskV2RailSubsection } from "./AskV2RailSubsection";
import { hasRailContent } from "./collectRailContent";
import { groupRailCitations } from "./groupRailCitations";
import {
  type AskV2RailConnector,
  type AskV2RailContent,
  type AskV2RailDocument,
  type AskV2RailMemory,
  type AskV2RailOutputItem,
  type AskV2RailSectionId,
  type AskV2RailSkill,
} from "./types";

import { CitedClaimsPanel } from "../../components/ask-puntt-cited-claims";
import { resolveReviewListCardThumbnail } from "../../components/ask-puntt-review/resolveReviewListCardThumbnail";
import { ReviewListCard } from "../../components/ask-puntt-review/ReviewListCard";
import { memorySearchTerm } from "../../routes/settings/routes/knowledge-database/memorySearch";
import { KNOWLEDGE_DATABASE_PATH } from "../../routes/settings/routes/knowledge-database/search";
import {
  CONNECTOR_CUSTOM_LOGOS,
  CONNECTOR_PHOSPHOR_ICONS,
  CONNECTOR_PHOSPHOR_WEIGHT,
} from "../../utils/askPunttConnectorLogos";
import { referenceDocumentTypeLabel } from "../../utils/askPunttProjectReferenceDocuments";
import { isDemoEnterprise } from "../../utils/auth";
import { downloadAsset } from "../../utils/http";
import { ASK_V2_CHAT_PATH, ASK_V2_KNOWLEDGE_DATABASE_SEARCH } from "../paths";
import { useAskPunttReview } from "../review/useAskPunttReview";

type AskV2RightRailProps = {
  content: AskV2RailContent;
  sectionOpen: Record<AskV2RailSectionId, boolean>;
  onSectionOpenChange: (id: AskV2RailSectionId, open: boolean) => void;
  onReviewOpen?: (reviewId: string) => void;
};

function ProgressIcon({ status }: { status: "pending" | "active" | "done" }) {
  if (status === "done") {
    return (
      <CheckCircle
        size={16}
        weight="fill"
        className="shrink-0 text-puntt-accent-9"
        aria-hidden="true"
      />
    );
  }
  if (status === "active") {
    return (
      <CircleNotch
        size={16}
        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
        aria-hidden="true"
      />
    );
  }
  return (
    <Circle
      size={16}
      className="shrink-0 text-puntt-neutral-gray-8"
      aria-hidden="true"
    />
  );
}

function MemoryScopeIcon({ scope }: { scope: AskV2RailMemory["scope"] }) {
  const { t } = useTranslation("ui");
  if (scope === "enterprise") {
    return (
      <Scroll
        size={16}
        className="mt-0.5 shrink-0 text-puntt-neutral-gray-10"
        aria-label={t("askV2.rail.memoryEnterprise")}
      />
    );
  }
  return (
    <SquaresFour
      size={16}
      className="mt-0.5 shrink-0 text-puntt-neutral-gray-10"
      aria-label={t("askV2.rail.memoryProject")}
    />
  );
}

function RailConnectorChip({ connector }: { connector: AskV2RailConnector }) {
  const PhosphorIcon = CONNECTOR_PHOSPHOR_ICONS[connector.id];
  const customLogoSrc = CONNECTOR_CUSTOM_LOGOS[connector.id];

  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
      data-testid={`ask-v2-rail-connector-${connector.id}`}
      aria-label={connector.name}
    >
      <span
        aria-hidden="true"
        className="inline-flex size-3.5 shrink-0 items-center justify-center"
      >
        {PhosphorIcon ? (
          <PhosphorIcon size={14} weight={CONNECTOR_PHOSPHOR_WEIGHT} />
        ) : customLogoSrc ? (
          <img
            src={customLogoSrc}
            alt=""
            width={14}
            height={14}
            className="size-3.5 object-contain"
            draggable={false}
          />
        ) : (
          <Buildings size={14} aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 truncate">{connector.name}</span>
    </span>
  );
}

function RailSkillChip({ skill }: { skill: AskV2RailSkill }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
      data-testid={`ask-v2-rail-skill-${skill.id}`}
      aria-label={skill.name}
    >
      <Sparkle
        size={14}
        className="shrink-0 text-puntt-neutral-gray-10"
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">{skill.name}</span>
    </span>
  );
}

function RailOutputReviewCard({
  item,
  onReviewOpen,
}: {
  item: AskV2RailOutputItem & { kind: "review" };
  onReviewOpen: (reviewId: string) => void;
}) {
  const { data } = useAskPunttReview(item.id);
  const live = data?.review;
  const thumbnailUrl = resolveReviewListCardThumbnail({
    review: live,
    payloadThumbnailUrl: item.thumbnailUrl,
    isDemoEnterprise: isDemoEnterprise(),
  });

  return (
    <ReviewListCard
      testIdPrefix="ask-v2-rail-output-review"
      data-testid={`ask-v2-rail-output-review-${item.id}`}
      onClick={() => onReviewOpen(item.id)}
      item={{
        id: item.id,
        name: live?.title?.trim() || item.title,
        versionNumber: item.versionNumber,
        thumbnailUrl,
        aiCommentCount: live?.commentsSummary?.ai ?? item.aiCommentCount,
        humanCommentCount:
          live?.commentsSummary?.human ?? item.humanCommentCount,
        isGenerating: item.isGenerating,
      }}
    />
  );
}

function RailDocumentCard({ document }: { document: AskV2RailDocument }) {
  const { t } = useTranslation("ui");
  const [downloading, setDownloading] = useState(false);
  const testId = `ask-v2-rail-document-${document.fileId}-v${document.versionOrdinal}`;
  const typeLabel = referenceDocumentTypeLabel(document.type, document.name, {
    image: t("campaignDocsPanel.fileTypeImage"),
    video: t("campaignDocsPanel.fileTypeVideo"),
    pdf: t("campaignDocsPanel.fileTypePdf"),
  });

  const handleDownload = async () => {
    if (downloading || !document.source) return;
    setDownloading(true);
    try {
      await downloadAsset(document.source, document.name);
    } catch {
      // downloadAsset already handles user-visible failure paths.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        void handleDownload();
      }}
      disabled={downloading || !document.source}
      data-testid={testId}
      aria-label={
        document.showVersionBadge
          ? t("campaignDocsPanel.downloadFileVersion", {
              name: document.name,
              version: document.versionOrdinal,
            })
          : t("campaignDocsPanel.downloadFile", { name: document.name })
      }
      className="flex w-full items-center gap-2 rounded-lg border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-2.5 py-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded border border-puntt-neutral-gray-5 bg-base-white text-[9px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11"
        data-testid={`${testId}-type`}
      >
        {typeLabel}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {document.name}
      </span>
      {document.showVersionBadge ? (
        <span
          className="shrink-0 rounded border border-puntt-neutral-gray-8 bg-puntt-neutral-gray-3 px-1.5 py-px text-[10px] font-medium text-puntt-neutral-gray-11"
          data-testid={`${testId}-version`}
        >
          V{document.versionOrdinal}
        </span>
      ) : null}
      {downloading ? (
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-neutral-gray-9"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}

/**
 * What KIND of evidence a rail row is, in a word and a colour.
 *
 * The three sections already separate them, but a row travels: the same
 * citation appears as a numbered chip under the message and as an inline link
 * in the prose. The tag is what makes "this is a memory, not a rule" readable
 * wherever the row is seen, rather than something inferred from which heading
 * it happened to sit under.
 */
/** One hue per kind, matching the inline link of the same evidence. */
const EVIDENCE_TAG_CLASS: Record<"Claim" | "Chat" | "Memory", string> = {
  Claim: "puntt-link",
  Chat: "puntt-link-chat",
  Memory: "puntt-link-memory",
};

/**
 * `rowId` is the row's own id, not its index: the chat and memory tags render
 * once per citation, so a rail with two cited chats would otherwise put the
 * same testid on two nodes and every locator for it resolves to 2 elements.
 * The Claim tag heads a section rather than a row, so it stays unsuffixed.
 */
function EvidenceTag({
  kind,
  rowId,
}: {
  kind: "Claim" | "Chat" | "Memory";
  rowId?: string;
}) {
  return (
    <span
      data-testid={`ask-v2-rail-evidence-tag-${kind.toLowerCase()}${rowId ? `-${rowId}` : ""}`}
      className={`shrink-0 rounded-full bg-puntt-neutral-gray-3 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${EVIDENCE_TAG_CLASS[kind]}`}
    >
      {kind}
    </span>
  );
}

export function AskV2RightRail({
  content,
  sectionOpen,
  onSectionOpenChange,
  onReviewOpen,
}: AskV2RightRailProps) {
  const { t } = useTranslation("ui");

  if (!hasRailContent(content)) return null;

  return (
    <aside
      className="ask-puntt-surface flex h-full w-[320px] shrink-0 flex-col gap-3 overflow-y-auto overscroll-contain p-3 [scrollbar-width:thin]"
      data-testid="ask-v2-right-rail"
    >
      {content.progress ? (
        <AskV2RailSection
          id="progress"
          title={t("askV2.rail.progress")}
          open={sectionOpen.progress}
          onOpenChange={(open) => onSectionOpenChange("progress", open)}
        >
          {content.progress.status ? (
            <p
              className="mb-3 text-[11px] font-medium text-puntt-neutral-gray-9"
              data-testid="ask-v2-rail-progress-status"
            >
              {content.progress.status}
            </p>
          ) : null}
          <ul
            className="flex flex-col gap-2"
            data-testid="ask-v2-rail-progress-list"
            aria-live="polite"
          >
            {content.progress.items.map((item) => (
              <li
                key={item.id}
                className="flex items-start gap-2 text-[13px] text-puntt-neutral-gray-12"
                data-testid={`ask-v2-rail-progress-item-${item.id}`}
                aria-label={t("askV2.rail.progressStepAria", {
                  label: item.label,
                  status: t(`askV2.rail.progressStepStatus.${item.status}`),
                })}
              >
                <ProgressIcon status={item.status} />
                <span className="min-w-0">
                  <span
                    className={`block leading-snug ${item.status === "done" ? "text-puntt-neutral-gray-10 line-through" : ""}`}
                  >
                    {item.label}
                  </span>
                  {item.detail ? (
                    <span className="block text-[12px] text-puntt-neutral-gray-10">
                      {item.detail}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </AskV2RailSection>
      ) : null}

      {content.output ? (
        <AskV2RailSection
          id="output"
          title={t("output")}
          open={sectionOpen.output}
          onOpenChange={(open) => onSectionOpenChange("output", open)}
        >
          <ul
            className="flex flex-col gap-2"
            data-testid="ask-v2-rail-output-list"
          >
            {content.output.items.map((item) => {
              const reviewCard =
                item.kind === "review" && onReviewOpen ? (
                  <RailOutputReviewCard
                    item={item as AskV2RailOutputItem & { kind: "review" }}
                    onReviewOpen={onReviewOpen}
                  />
                ) : null;

              return (
                <li
                  key={item.id}
                  data-testid={`ask-v2-rail-output-item-${item.id}`}
                >
                  {reviewCard ?? (
                    <div className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2">
                      {item.thumbnailUrl ? (
                        <img
                          src={item.thumbnailUrl}
                          alt=""
                          className="size-10 shrink-0 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-10">
                          <FileText size={16} aria-hidden="true" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-puntt-neutral-gray-12">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </AskV2RailSection>
      ) : null}

      {content.context ? (
        <AskV2RailSection
          id="context"
          title={t("context")}
          open={sectionOpen.context}
          onOpenChange={(open) => onSectionOpenChange("context", open)}
        >
          <div
            className="flex flex-col gap-4"
            data-testid="ask-v2-rail-context-body"
          >
            {content.context.overview ? (
              <div data-testid="ask-v2-rail-overview">
                <h3 className="mb-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("askV2.rail.overview")}
                </h3>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.overview}
                </p>
              </div>
            ) : null}
            {content.context.upcomingWork ? (
              <div data-testid="ask-v2-rail-upcoming">
                <h3 className="mb-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("campaignDocsPanel.nextSteps")}
                </h3>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.upcomingWork}
                </p>
              </div>
            ) : null}
            {(() => {
              // One list held rules, conversations and memories at once, and
              // only the rules were linkable. Split by what the evidence IS,
              // so each row can go somewhere a reader can verify it.
              const cited = groupRailCitations(content.context.citedClaims);
              return (
                <>
                  {cited.claims.length > 0 ? (
                    <div data-testid="ask-v2-rail-cited-claims">
                      <div className="mb-1">
                        <EvidenceTag kind="Claim" />
                      </div>
                      <CitedClaimsPanel
                        citations={cited.claims}
                        variant="section"
                        testIdPrefix="ask-v2-rail-cited-claims"
                      />
                    </div>
                  ) : null}
                  {cited.chats.length > 0 ? (
                    <AskV2RailSubsection id="cited-chats" title="Chats">
                      <ul
                        className="flex flex-col gap-2"
                        data-testid="ask-v2-rail-cited-chats"
                      >
                        {cited.chats.map((chat) => (
                          <li
                            key={chat.id}
                            className="flex items-start gap-1.5"
                          >
                            <EvidenceTag kind="Chat" rowId={chat.id} />
                            <RouterLink
                              to={ASK_V2_CHAT_PATH}
                              params={{ chatId: chat.id }}
                              data-testid={`ask-v2-rail-cited-chat-${chat.id}`}
                              aria-label={`Open the conversation ${chat.title || chat.id}`}
                              className="puntt-link-chat text-[13px] leading-snug underline decoration-dotted underline-offset-2"
                            >
                              {chat.title || "Untitled conversation"}
                            </RouterLink>
                          </li>
                        ))}
                      </ul>
                    </AskV2RailSubsection>
                  ) : null}
                  {cited.memories.length > 0 ? (
                    <AskV2RailSubsection id="cited-memories" title="Memory">
                      <ul
                        className="flex flex-col gap-2"
                        data-testid="ask-v2-rail-cited-memories"
                      >
                        {cited.memories.map((memory) => (
                          <li
                            key={memory.id}
                            className="flex items-start gap-1.5"
                          >
                            <EvidenceTag kind="Memory" rowId={memory.id} />
                            <RouterLink
                              to={KNOWLEDGE_DATABASE_PATH}
                              search={{
                                ...ASK_V2_KNOWLEDGE_DATABASE_SEARCH,
                                tab: "memories",
                                ...(memorySearchTerm(memory.text)
                                  ? { q: memorySearchTerm(memory.text) }
                                  : {}),
                              }}
                              data-testid={`ask-v2-rail-cited-memory-${memory.id}`}
                              aria-label={`Open this memory in the Knowledge Database: ${memory.text}`}
                              className="puntt-link-memory text-[13px] leading-snug underline decoration-dotted underline-offset-2"
                            >
                              {memory.text}
                            </RouterLink>
                          </li>
                        ))}
                      </ul>
                    </AskV2RailSubsection>
                  ) : null}
                </>
              );
            })()}
            {content.context.memories.length > 0 ? (
              <AskV2RailSubsection
                id="memories"
                title={t("askV2.rail.memories")}
              >
                <ul
                  className="flex flex-col gap-3"
                  data-testid="ask-v2-rail-memories"
                >
                  {content.context.memories.map((memory) => (
                    <li
                      key={memory.scope}
                      className="flex items-start gap-2 text-[13px] leading-snug text-puntt-neutral-gray-12"
                      data-testid={`ask-v2-rail-memory-${memory.scope}`}
                    >
                      <MemoryScopeIcon scope={memory.scope} />
                      <span className="line-clamp-3 min-w-0">
                        {memory.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </AskV2RailSubsection>
            ) : null}
            {content.context.documents.length > 0 ? (
              <AskV2RailSubsection
                id="documents"
                title={t("campaignDocsPanel.referenceDocuments")}
              >
                <ul
                  className="flex flex-col gap-2"
                  data-testid="ask-v2-rail-documents"
                >
                  {content.context.documents.map((document) => (
                    <li key={document.id}>
                      <RailDocumentCard document={document} />
                    </li>
                  ))}
                </ul>
              </AskV2RailSubsection>
            ) : null}
            {content.context.connectors.length > 0 ? (
              <AskV2RailSubsection id="connectors" title={t("connectors")}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="ask-v2-rail-connectors"
                >
                  {content.context.connectors.map((connector) => (
                    <RailConnectorChip
                      key={connector.id}
                      connector={connector}
                    />
                  ))}
                </div>
              </AskV2RailSubsection>
            ) : null}
            {content.context.skills.length > 0 ? (
              <AskV2RailSubsection id="skills" title={t("askV2.rail.skills")}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="ask-v2-rail-skills"
                >
                  {content.context.skills.map((skill) => (
                    <RailSkillChip key={skill.id} skill={skill} />
                  ))}
                </div>
              </AskV2RailSubsection>
            ) : null}
          </div>
        </AskV2RailSection>
      ) : null}
    </aside>
  );
}
